import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react'
import { RouletteWheelFace } from '@/components/LandingLuckyWheel/RouletteWheelFace'
import { RouletteIcon } from '@/components/LandingLuckyWheel/RouletteIcon'
import { buildRoulettePalette } from '@/components/LandingLuckyWheel/rouletteGeometry'
import { useRouletteMotion } from '@/components/LandingLuckyWheel/useRouletteMotion'
import { useRouletteSound } from '@/components/LandingLuckyWheel/useRouletteSound'
import { buildLuckyWheelItems } from '@/lib/buildLuckyWheelItems'
import { campaignBrandPaletteToStyle } from '@/lib/campaignBrandPalette'
import type { ApiRouletteResult, ApiRouletteVisual } from './types'

export function ApiRouletteWheel({
  configuration,
  onSpin,
  preview = false,
  bare = false,
  repeatable = false,
  holdMs = 2400,
  onAcknowledge,
  onResult,
}: {
  configuration: ApiRouletteVisual
  onSpin?: () => Promise<ApiRouletteResult>
  preview?: boolean
  bare?: boolean
  repeatable?: boolean
  holdMs?: number
  onAcknowledge?: (result: ApiRouletteResult) => Promise<void>
  onResult?: (result: ApiRouletteResult) => void
}) {
  const wheelId = `api-wheel-${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  const [spinning, setSpinning] = useState(false)
  const [result, setResult] = useState<ApiRouletteResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [completed, setCompleted] = useState(false)
  const [celebrating, setCelebrating] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [needsConfirm, setNeedsConfirm] = useState(false)
  const pendingWin = useRef<ApiRouletteResult | null>(null)
  const lock = useRef(false)
  const resetTimer = useRef<number | null>(null)
  const sound = useRouletteSound()
  const motion = useRouletteMotion(sound.tick)
  const visual = result?.configuration ?? configuration
  const items = useMemo(
    () =>
      buildLuckyWheelItems(
        {
          positions: visual.slices.length,
          prizes: visual.slices.map((slice, index) => ({
            position: index + 1,
            prize_type: 'none',
            display: { label: slice.label },
          })),
        },
        wheelId
      ),
    [visual.slices, wheelId]
  )
  const palette = useMemo(
    () => buildRoulettePalette(visual.appearance.primary_color),
    [visual.appearance.primary_color]
  )

  useEffect(
    () => () => {
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current)
    },
    []
  )

  const releaseWheel = () => {
    setCelebrating(false)
    setCompleted(false)
    setResult(null)
  }
  const hubLabel = spinning
    ? 'Girando…'
    : confirming
      ? 'Confirmando…'
      : needsConfirm
        ? 'Confirmar prêmio'
        : celebrating
          ? 'Você ganhou'
          : !preview && completed && !repeatable
            ? 'Giro registrado'
            : 'GIRAR'

  const confirmWin = async (next: ApiRouletteResult) => {
    if (!onAcknowledge) return
    setConfirming(true)
    setError(null)
    try {
      await onAcknowledge(next)
      pendingWin.current = null
      setNeedsConfirm(false)
    } catch (err) {
      pendingWin.current = next
      setNeedsConfirm(true)
      setError(
        err instanceof Error ? err.message : 'Não foi possível confirmar o prêmio. Tente novamente.'
      )
      throw err
    } finally {
      setConfirming(false)
    }
  }

  const finishSpin = async (next: ApiRouletteResult) => {
    sound.finish(next.won)
    onResult?.(next)
    if (preview) return
    setCompleted(true)
    if (!next.won) {
      if (repeatable) resetTimer.current = window.setTimeout(releaseWheel, Math.min(holdMs, 900))
      return
    }
    setCelebrating(true)
    try {
      await confirmWin(next)
    } catch {
      return
    }
    if (repeatable) resetTimer.current = window.setTimeout(releaseWheel, holdMs)
  }

  const spin = async () => {
    if (lock.current || confirming) return
    if (pendingWin.current) {
      lock.current = true
      try {
        await confirmWin(pendingWin.current)
        if (repeatable) resetTimer.current = window.setTimeout(releaseWheel, holdMs)
      } catch {
        /* The alert already explains the failed confirmation. */
      } finally {
        lock.current = false
      }
      return
    }
    if (!preview && (!onSpin || (completed && !repeatable))) return
    if (completed && repeatable && celebrating) return
    lock.current = true
    setError(null)
    setCelebrating(false)
    setSpinning(true)
    sound.unlock()
    motion.start(items.length)
    try {
      const next = preview
        ? {
            attempt_id: 0,
            won: visual.slices[0].kind === 'prize',
            position: 1,
            slice: visual.slices[0],
            configuration: visual,
            created_at: '',
          }
        : await onSpin!()
      setResult(next)
      if (next.configuration.slices.length !== items.length)
        motion.start(next.configuration.slices.length)
      if (await motion.land(next.position)) await finishSpin(next)
    } catch (err) {
      motion.cancel()
      setError(
        err instanceof Error
          ? err.message
          : 'Falha no giro. Tente novamente com a mesma autorização.'
      )
    } finally {
      lock.current = false
      setSpinning(false)
    }
  }

  // Hex colors are validated server-side; the selectors affect only this new instance.
  const sliceCss = visual.slices
    .map(
      (slice, index) =>
        `#${wheelId} .roulette-sector:nth-child(${index + 1})>path:first-child{fill:${/^#[a-f0-9]{6}$/i.test(slice.color) ? slice.color : '#5e17eb'}}#${wheelId} .roulette-sector:nth-child(${index + 1})>g{color:${/^#[a-f0-9]{6}$/i.test(slice.text_color) ? slice.text_color : '#ffffff'}}`
    )
    .join('')
  const wheelStyle = {
    ...campaignBrandPaletteToStyle(palette, visual.appearance.button_text_color),
    '--roulette-action-background': visual.appearance.button_color,
  } as CSSProperties
  return (
    <div id={wheelId} className="api-roulette-wheel" style={wheelStyle}>
      <style>{sliceCss}</style>
      {bare ? null : <h3>{visual.appearance.title}</h3>}
      <div className="roulette-wheel api-roulette-stage">
        <RouletteWheelFace
          items={items}
          palette={palette}
          rotorRef={motion.rotorRef}
          pointerRef={motion.pointerRef}
          selectedPosition={result?.position}
          spinning={spinning}
        />
        <button
          type="button"
          className="roulette-hub"
          aria-label={hubLabel}
          aria-busy={spinning || confirming}
          disabled={
            spinning ||
            confirming ||
            (celebrating && !needsConfirm) ||
            (!needsConfirm && !preview && !onSpin) ||
            (!needsConfirm && !preview && !repeatable && completed)
          }
          onClick={() => void spin()}
        >
          <span className="roulette-hub-symbol" aria-hidden="true">
            {spinning ? (
              <span className="roulette-hub-loader" />
            ) : (
              <RouletteIcon kind="arrow" />
            )}
          </span>
          <span>
            {spinning ? 'GIRANDO' : hubLabel === 'GIRAR' ? 'GIRAR' : hubLabel === 'Você ganhou' ? 'GANHOU' : hubLabel === 'Giro registrado' ? 'REGISTRADO' : hubLabel === 'Confirmando…' ? '…' : 'CONFIRMAR'}
          </span>
        </button>
        {celebrating && result?.won ? (
          <div className="api-roulette-win" role="status">
            <div className="roulette-confetti" aria-hidden="true">
              {Array.from({ length: 18 }, (_, index) => (
                <i
                  key={index}
                  style={
                    {
                      '--x': `${((index * 37) % 180) - 90}px`,
                      '--y': `${40 + ((index * 23) % 120)}px`,
                      '--r': `${index * 47}deg`,
                      '--delay': `${(index % 6) * 0.05}s`,
                      '--confetti-color': index % 2 === 0 ? '#e6bc65' : '#ffffff',
                    } as CSSProperties
                  }
                />
              ))}
            </div>
            <p>Você ganhou</p>
            <strong>{result.slice.label}</strong>
          </div>
        ) : null}
      </div>
      {!bare && preview && <p>Prévia visual. Este giro não participa nem concede prêmio.</p>}
      {!bare && completed && result && !spinning && (
        <p role="status">
          <strong>{result.won ? 'Você ganhou!' : 'Não foi desta vez.'}</strong>{' '}
          {result.slice.label}
        </p>
      )}
      {error && <p role="alert">{error}</p>}
    </div>
  )
}
